/**
 * 🎙️ Service Audio pour Chat ADHA
 * 
 * Gère la transcription vocale (STT) et la synthèse audio (TTS)
 * 
 * Endpoints API requis:
 * - POST /audio/transcribe/ → Speech-to-Text
 * - POST /audio/synthesize/ → Text-to-Speech
 */

// ============================================================================
// TYPES
// ============================================================================

export type TTSVoice = 'nova' | 'alloy' | 'echo' | 'onyx';
export type AudioFormat = 'mp3' | 'opus' | 'aac' | 'flac' | 'wav' | 'pcm';

export interface TranscriptionResult {
  text: string;
  language?: string;
  confidence?: number;
  duration?: number;
}

export interface SynthesisOptions {
  voice?: TTSVoice;
  speed?: number; // 0.25 à 4.0
  format?: AudioFormat;
}

export interface AudioError {
  code: string;
  message: string;
  details?: string;
}

// État de l'enregistrement
export type RecordingState = 'idle' | 'recording' | 'processing' | 'error';

// ============================================================================
// CONSTANTES
// ============================================================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const AUDIO_ENDPOINTS = {
  TRANSCRIBE: '/audio/transcribe/',
  SYNTHESIZE: '/audio/synthesize/',
};

const DEFAULT_VOICE: TTSVoice = 'nova';
const DEFAULT_SPEED = 1.0;
const DEFAULT_FORMAT: AudioFormat = 'mp3';

// Contraintes audio pour l'enregistrement (optimisées pour la transcription)
const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  sampleRate: 16000,
  channelCount: 1
};

// ============================================================================
// SERVICE AUDIO
// ============================================================================

class AudioService {
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private recordingState: RecordingState = 'idle';
  private stateCallbacks: Set<(state: RecordingState) => void> = new Set();

  // --------------------------------------------------------------------------
  // TRANSCRIPTION (Speech-to-Text)
  // --------------------------------------------------------------------------

  /**
   * Transcrit un fichier audio en texte
   * @param audioFile - Fichier audio à transcrire (Blob ou File)
   * @param language - Code langue optionnel (fr, en, etc.)
   */
  async transcribe(
    audioFile: Blob | File,
    language?: string
  ): Promise<TranscriptionResult> {
    const formData = new FormData();
    formData.append('audio', audioFile, 'recording.webm');
    
    if (language) {
      formData.append('language', language);
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}${AUDIO_ENDPOINTS.TRANSCRIBE}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erreur de transcription');
      }

      return await response.json();
    } catch (error) {
      console.error('[AudioService] ❌ Erreur transcription:', error);
      throw error;
    }
  }

  // --------------------------------------------------------------------------
  // SYNTHÈSE (Text-to-Speech)
  // --------------------------------------------------------------------------

  /**
   * Convertit du texte en audio
   * @param text - Texte à synthétiser
   * @param options - Options de synthèse (voix, vitesse, format)
   */
  async synthesize(
    text: string,
    options: SynthesisOptions = {}
  ): Promise<Blob> {
    const {
      voice = DEFAULT_VOICE,
      speed = DEFAULT_SPEED,
      format = DEFAULT_FORMAT
    } = options;

    try {
      const response = await fetch(
        `${API_BASE_URL}${AUDIO_ENDPOINTS.SYNTHESIZE}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            text,
            voice,
            speed,
            format
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erreur de synthèse');
      }

      return await response.blob();
    } catch (error) {
      console.error('[AudioService] ❌ Erreur synthèse:', error);
      throw error;
    }
  }

  /**
   * Joue un blob audio
   */
  async playAudio(audioBlob: Blob): Promise<void> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(audioBlob);
      const audio = new Audio(url);
      
      audio.onended = () => {
        URL.revokeObjectURL(url);
        resolve();
      };
      
      audio.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      
      audio.play();
    });
  }

  // --------------------------------------------------------------------------
  // ENREGISTREMENT AUDIO
  // --------------------------------------------------------------------------

  /**
   * Démarre l'enregistrement audio
   */
  async startRecording(): Promise<void> {
    try {
      this.setRecordingState('recording');
      this.audioChunks = [];

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: AUDIO_CONSTRAINTS
      });

      this.mediaRecorder = new MediaRecorder(stream, {
        mimeType: this.getSupportedMimeType()
      });

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onerror = (event) => {
        console.error('[AudioService] ❌ Erreur MediaRecorder:', event);
        this.setRecordingState('error');
      };

      this.mediaRecorder.start(100); // Chunks de 100ms
      console.log('[AudioService] 🎙️ Enregistrement démarré');
    } catch (error) {
      console.error('[AudioService] ❌ Erreur démarrage enregistrement:', error);
      this.setRecordingState('error');
      throw error;
    }
  }

  /**
   * Arrête l'enregistrement et retourne le blob audio
   */
  async stopRecording(): Promise<Blob> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('Aucun enregistrement en cours'));
        return;
      }

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, {
          type: this.getSupportedMimeType()
        });
        
        // Arrêter les pistes audio
        this.mediaRecorder?.stream.getTracks().forEach(track => track.stop());
        
        this.setRecordingState('idle');
        console.log('[AudioService] ⏹️ Enregistrement arrêté');
        resolve(audioBlob);
      };

      this.mediaRecorder.stop();
    });
  }

  /**
   * Annule l'enregistrement en cours
   */
  cancelRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
      this.mediaRecorder.stop();
      this.audioChunks = [];
      this.setRecordingState('idle');
      console.log('[AudioService] 🚫 Enregistrement annulé');
    }
  }

  // --------------------------------------------------------------------------
  // UTILITAIRES
  // --------------------------------------------------------------------------

  /**
   * Vérifie si le navigateur supporte l'enregistrement audio
   */
  isSupported(): boolean {
    try {
      return typeof navigator !== 'undefined' 
        && 'mediaDevices' in navigator 
        && 'getUserMedia' in navigator.mediaDevices
        && typeof MediaRecorder !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Retourne l'état actuel de l'enregistrement
   */
  getRecordingState(): RecordingState {
    return this.recordingState;
  }

  /**
   * S'abonne aux changements d'état
   */
  onStateChange(callback: (state: RecordingState) => void): () => void {
    this.stateCallbacks.add(callback);
    return () => this.stateCallbacks.delete(callback);
  }

  /**
   * Retourne le mime type supporté par le navigateur
   */
  private getSupportedMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4'
    ];
    
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    
    return 'audio/webm';
  }

  /**
   * Met à jour l'état d'enregistrement et notifie les abonnés
   */
  private setRecordingState(state: RecordingState): void {
    this.recordingState = state;
    this.stateCallbacks.forEach(cb => cb(state));
  }

  /**
   * Récupère le token d'authentification
   */
  private getAuthToken(): string {
    return localStorage.getItem('auth_token') || '';
  }
}

// Export singleton
export const audioService = new AudioService();
export default audioService;
